/**
 * Header.tsx - Application Header Component
 */

import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuthContext } from '@/contexts/AuthContext'
import { useCircleContext } from '@/contexts/CircleContext'
import { useAutoPlayAudio } from '@/contexts/ChatContext'
import { useFilterContext } from '@/contexts/FilterContext'
import { AvatarImage } from '@/ui/components/AvatarImage'
import {
    Box,
    Button,
    Heading,
    HStack,
    IconButton,
    MenuContent,
    MenuItem,
    MenuPositioner,
    MenuRoot,
    MenuTrigger,
    Portal,
    TooltipContent,
    TooltipPositioner,
    TooltipRoot,
    TooltipTrigger,
} from "@chakra-ui/react"
import { getAvatarUrl } from '@/utilities/imageUtils'
import { useProfile } from 'core'
import { useTranslation } from 'react-i18next'
import { LuLanguages, LuPanelLeftClose, LuPanelLeftOpen, LuPlus, LuVolume2, LuVolumeX } from 'react-icons/lu'
import iqraIcon from '/iqra.svg'
import { htmlContainsArabicOrKurdish } from '@/utilities/detectScript'
import { useParentChrome } from '@/utilities/useParentChrome'
import { ShareButton } from './ShareButton'
import { DownloadButton } from './DownloadButton'

const supportedLanguages = [
    { value: 'en', label: 'English' },
    { value: 'ar', label: 'العربية' },
    { value: 'ckb', label: 'کوردی' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'cn', label: '中文' },
    { value: 'ru', label: 'Русский' },
    { value: 'ur', label: 'اردو' },
    { value: 'pl', label: 'Polski' },
];

interface HeaderProps {
    setPostId?: (id: string) => void;
    sidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({ sidebarOpen, setSidebarOpen }) => {
    const { profileName } = useAuthContext()
    const { parentCircleName, parentCircleImageUrl } = useCircleContext()
    const { autoPlayAudio, setAutoPlayAudio } = useAutoPlayAudio()
    const { resetFilters } = useFilterContext()
    const navigate = useNavigate()
    const { circleId } = useParams()

    // Mirrors RecentChatsView's New Chat: navigate AND clear filters so a new
    // chat starts clean (the sidebar button does this; the mobile header must too).
    const handleNewChat = () => {
        navigate(`/aichat/${circleId}/new`)
        resetFilters()
    }
    const { t, i18n } = useTranslation()
    const hasParentChrome = useParentChrome()

    const { data: profile } = useProfile()
    const profileImageUrl = getAvatarUrl(profile?.avatar)
    const hasArabicOrKurdish = useMemo(() => {
        if (parentCircleName) {
          return htmlContainsArabicOrKurdish(parentCircleName)
        }
        return false
      }, [parentCircleName])

    const goToBookCair = () => {
        window.open(`${window.location.origin}/book-cair-dashboard`, '_blank')
    }

    return (
        <>
            <HStack
                className="fade"
                padding={2}
                height="var(--header-height)"
                position="sticky"
                top="0"
                zIndex="99"
                background="var(--cair-surface-elevated)"
                borderBottom="1px solid var(--cair-border)"
                boxShadow="var(--cair-shadow-sm)"
            >
                <IconButton
                    aria-label="Toggle Sidebar"
                    variant="ghost"
                    color="#333333"
                    _hover={{ background: "var(--cair-accent-subtle)" }}
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                    {sidebarOpen ? <LuPanelLeftClose /> : <LuPanelLeftOpen />}
                </IconButton>
                {/* New chat — surfaced in the header on mobile, where the
                    sidebar (and its New Chat button) is collapsed by default. */}
                <IconButton
                    aria-label={t("new_chat") || "New chat"}
                    title={t("new_chat") || "New chat"}
                    display={{ base: "inline-flex", md: "none" }}
                    variant="ghost"
                    color="#333333"
                    _hover={{ background: "var(--cair-accent-subtle)" }}
                    onClick={handleNewChat}
                >
                    <LuPlus />
                </IconButton>
                {/* Circle avatar + name. Hidden when embedded — the breadcrumb
                    above the iframe already shows the circle, so this would be redundant. */}
                {!hasParentChrome && (
                    <HStack
                        className="cursor-pointer"
                        onClick={() => navigate(`/aichat/${circleId}/new`)}
                        fontSize={"1rem"}
                        minWidth={0}
                        overflow="hidden"
                    >
                        <AvatarImage size="md" avatarFallbackName={parentCircleName} avatarImageUrl={parentCircleImageUrl} />
                        <Heading
                            marginRight={2}
                            fontSize="md"
                            className={`${hasArabicOrKurdish ? 'arabic-kurdish-font' : 'kv-text-font'} align-center`}
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            maxWidth="100%"
                        >
                            {parentCircleName}
                        </Heading>
                    </HStack>
                )}

                {/* Audio + translate controls (moved here from the sidebar) */}
                <HStack gap={1} flexShrink={0}>
                    <ShareButton />
                    <DownloadButton />

                    <TooltipRoot openDelay={500} positioning={{ placement: "bottom" }}>
                        <TooltipTrigger asChild>
                            <IconButton
                                aria-label={t("toggle_autoplay") || "Toggle auto-play"}
                                variant="ghost"
                                size="sm"
                                width="36px"
                                height="36px"
                                color={autoPlayAudio ? "var(--cair-accent)" : "#333333"}
                                background={autoPlayAudio ? "var(--cair-accent-subtle)" : "transparent"}
                                _hover={{ background: "var(--cair-accent-subtle)" }}
                                onClick={() => setAutoPlayAudio(!autoPlayAudio)}
                            >
                                {autoPlayAudio ? <LuVolume2 /> : <LuVolumeX />}
                            </IconButton>
                        </TooltipTrigger>
                        <Portal>
                            <TooltipPositioner>
                                <TooltipContent>
                                    {t("autoplay_tooltip") || "Auto-play audio on live chat after full message is rendered"}
                                </TooltipContent>
                            </TooltipPositioner>
                        </Portal>
                    </TooltipRoot>

                    <MenuRoot positioning={{ placement: "bottom-end" }}>
                        <MenuTrigger asChild>
                            <IconButton
                                aria-label={t("change_language") || "Change language"}
                                title={t("change_language") || "Change language"}
                                variant="ghost"
                                size="sm"
                                width="36px"
                                height="36px"
                                color="#333333"
                                _hover={{ background: "var(--cair-accent-subtle)" }}
                            >
                                <LuLanguages />
                            </IconButton>
                        </MenuTrigger>
                        <Portal>
                            <MenuPositioner>
                                <MenuContent
                                    zIndex={9999}
                                    backgroundColor="white"
                                    borderRadius="8px"
                                    padding="8px"
                                    boxShadow="0 4px 16px rgba(0, 0, 0, 0.12)"
                                    maxHeight="320px"
                                    overflowY="auto"
                                >
                                    {supportedLanguages.map((lang) => (
                                        <MenuItem key={lang.value} value={lang.value} onClick={() => i18n.changeLanguage(lang.value)}>
                                            {lang.label}
                                        </MenuItem>
                                    ))}
                                </MenuContent>
                            </MenuPositioner>
                        </Portal>
                    </MenuRoot>
                </HStack>

                <Box margin="auto"></Box>

                {/* Mobile: the breadcrumb above the iframe is hidden, so surface
                    "Go to BOOK CAIR" here on the opposite side of the audio/translate. */}
                <Button
                    display={{ base: "inline-flex", md: "none" }}
                    variant="ghost"
                    size="sm"
                    color="#333333"
                    flexShrink={0}
                    _hover={{ background: "var(--cair-accent-subtle)" }}
                    onClick={goToBookCair}
                >
                    <img src={iqraIcon} alt="" width={18} height={18} />
                    {t("go_to_book_cair") || "Go to BOOK CAIR"}
                </Button>

                {/* Desktop, standalone (no parent breadcrumb): Go to BOOK CAIR + avatar */}
                {!hasParentChrome && (
                    <Box display={{ base: "none", md: "flex" }} alignItems="center" gap={2}>
                        <Button
                            variant="ghost"
                            size="sm"
                            color="#333333"
                            _hover={{ background: "var(--cair-accent-subtle)" }}
                            onClick={goToBookCair}
                        >
                            {t("go_to_book_cair") || "Go to BOOK CAIR"}
                            <img src={iqraIcon} alt="" width={18} height={18} />
                        </Button>
                        <AvatarImage size="md" avatarFallbackName={profileName} avatarImageUrl={profileImageUrl} />
                    </Box>
                )}
            </HStack>
        </>
    )
}
